import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import SchemaOrg from '@/components/base/SchemaOrg';

export default function About() {
  const { t } = useTranslation();
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formMessage, setFormMessage] = useState('');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const breadcrumbItems = [
    { label: t('common.breadcrumbHome'), href: '/' },
    { label: t('about.title') },
  ];

  const handleContactSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.elements.namedItem('company_alt') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setFormStatus('success');
      setFormMessage('提交成功！我们会尽快回复你。');
      form.reset();
      return;
    }

    setFormStatus('loading');
    try {
      const formData = new FormData(form);
      formData.delete('company_alt');
      const response = await fetch('https://readdy.ai/api/form/d9rc8hvnahb7382ovkfg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setFormMessage('提交成功！感谢你的留言，我们会尽快回复你。');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || '提交失败，请稍后重试。';
        setFormStatus('error');
        setFormMessage(serverMsg);
      }
    } catch {
      setFormStatus('error');
      setFormMessage('网络错误，请检查连接后重试。');
    }
  };

  const orgSchema = {
    name: t('common.siteTitle'),
    url: 'https://family-relationships-guide.com',
    description: t('about.brandDescription'),
    sameAs: ['https://weibo.com/family-guide'],
    contactPoint: {
      '@type': 'ContactPoint',
      email: 'hello@family-guide.com',
      contactType: 'customer service',
    },
  };

  return (
    <>
      <SchemaOrg type="Organization" data={orgSchema} />
      <SchemaOrg
        type="WebPage"
        data={{
          name: `${t('about.title')} - ${t('common.siteTitle')}`,
          description: t('about.brandDescription'),
          inLanguage: 'zh-CN',
          breadcrumb: {
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: t('common.breadcrumbHome'), item: 'https://family-relationships-guide.com/' },
              { '@type': 'ListItem', position: 2, name: t('about.title') },
            ],
          },
        }}
      />
      <Navbar />
      <main className="pt-20">
        <Breadcrumb items={breadcrumbItems} />

        <section className="w-full px-4 md:px-6 py-12 md:py-16 bg-background-50">
          <div className="max-w-4xl mx-auto">
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-8">
              {t('about.title')}
            </h1>

            <div className="space-y-12">
              <div>
                <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-900 mb-4">
                  {t('about.brandTitle')}
                </h2>
                <p className="text-foreground-700 leading-relaxed text-sm md:text-base">
                  {t('about.brandDescription')}
                </p>
                <p className="text-foreground-700 leading-relaxed text-sm md:text-base mt-4">
                  我们的团队由资深心理咨询师、家庭关系教育者和重组家庭亲历者组成。我们经历过重组家庭的迷茫与挑战，也见证了关系修复后的温暖与力量。带着这份切身的理解与专业的知识，我们创立了这个平台，希望为每一个在家庭关系中努力的人提供一盏灯。
                </p>
                <p className="text-foreground-700 leading-relaxed text-sm md:text-base mt-4">
                  我们相信，重组家庭不是\u201C破碎\u201D的，而是\u201C重新组合\u201D的。每个重组家庭都有其独特的美丽与可能性——只要我们愿意用理解和耐心去浇灌，用科学的方法去经营。
                </p>
              </div>

              <div>
                <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-900 mb-4">
                  {t('about.reputationTitle')}
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                  <div className="bg-background-50 border border-background-200/70 rounded-lg p-6 text-center">
                    <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center rounded-full bg-primary-100 text-primary-600">
                      <i className="ri-user-star-line text-xl"></i>
                    </div>
                    <div className="text-3xl font-bold text-foreground-900 mb-1">50,000+</div>
                    <p className="text-sm text-foreground-600">每月活跃读者</p>
                  </div>
                  <div className="bg-background-50 border border-background-200/70 rounded-lg p-6 text-center">
                    <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center rounded-full bg-accent-100 text-accent-600">
                      <i className="ri-article-line text-xl"></i>
                    </div>
                    <div className="text-3xl font-bold text-foreground-900 mb-1">200+</div>
                    <p className="text-sm text-foreground-600">原创深度文章</p>
                  </div>
                  <div className="bg-background-50 border border-background-200/70 rounded-lg p-6 text-center">
                    <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-600">
                      <i className="ri-hearts-line text-xl"></i>
                    </div>
                    <div className="text-3xl font-bold text-foreground-900 mb-1">98%</div>
                    <p className="text-sm text-foreground-600">读者好评率</p>
                  </div>
                </div>
              </div>

              <div>
                <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-900 mb-4">
                  {t('about.contactTitle')}
                </h2>
                <p className="text-foreground-700 leading-relaxed text-sm md:text-base mb-6">
                  {t('about.contactDescription')}
                </p>

                <form
                  onSubmit={handleContactSubmit}
                  data-readdy-form
                  className="max-w-lg space-y-4"
                >
                  <input
                    type="text"
                    name="company_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                    className="honeypot-field"
                  />
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">姓名</label>
                      <input
                        id="contact-name"
                        type="text"
                        name="name"
                        required
                        className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-800 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">邮箱</label>
                      <input
                        id="contact-email"
                        type="email"
                        name="email"
                        required
                        className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-800 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                      />
                    </div>
                  </div>
                  <div>
                    <label htmlFor="contact-subject" className="block text-sm font-medium text-foreground-700 mb-1.5">主题</label>
                    <input
                      id="contact-subject"
                      type="text"
                      name="subject"
                      required
                      className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-800 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">留言内容</label>
                    <textarea
                      id="contact-message"
                      name="message"
                      rows={5}
                      maxLength={500}
                      required
                      className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-800 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all resize-none"
                    ></textarea>
                    <p className="text-xs text-foreground-500 mt-1">最多500个字符</p>
                  </div>
                  <button
                    type="submit"
                    disabled={formStatus === 'loading'}
                    className="bg-primary-500 hover:bg-primary-600 disabled:bg-primary-300 text-white px-8 py-3 rounded-full text-sm font-medium transition-all duration-300 cursor-pointer whitespace-nowrap"
                  >
                    {formStatus === 'loading' ? '提交中...' : '发送留言'}
                  </button>
                  {formStatus !== 'idle' && (
                    <p className={`text-sm ${formStatus === 'success' ? 'text-secondary-700' : 'text-accent-600'}`}>
                      {formMessage}
                    </p>
                  )}
                </form>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}